/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { notificationsAPI } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Bell,
  Check,
  CheckCheck,
  FileText,
  Building2,
  Calendar,
  Megaphone,
  AlertCircle,
  Loader2,
  Inbox,
} from 'lucide-react';

const NotificationsPage = () => {
  const { user } = useAuth();
  usePageTitle('Bildirimler');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll({ limit: 50 });
      setNotifications(response.data.notifications);
      setTotal(response.data.total);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      toast.error('Bildirimler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('Tüm bildirimler okundu olarak işaretlendi');
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      ticket_created: FileText,
      ticket_updated: FileText,
      stk_application: Building2,
      stk_approved: Building2,
      stk_rejected: Building2,
      stk_document_request: Building2,
      meeting: Calendar,
      announcement: Megaphone,
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type) => {
    const colors = {
      ticket_created: 'bg-blue-100 text-blue-600',
      ticket_updated: 'bg-blue-100 text-blue-600',
      stk_application: 'bg-purple-100 text-purple-600',
      stk_approved: 'bg-emerald-100 text-emerald-600',
      stk_rejected: 'bg-red-100 text-red-600',
      stk_document_request: 'bg-amber-100 text-amber-600',
      meeting: 'bg-green-100 text-green-600',
      announcement: 'bg-amber-100 text-amber-600',
    };
    return colors[type] || 'bg-slate-100 text-slate-600';
  };

  const getNotificationLink = (notification) => {
    const links = {
      ticket_created: `/panel/talepler/${notification.related_id}`,
      ticket_updated: `/panel/talepler/${notification.related_id}`,
      stk_application: `/panel/stk/${notification.related_id}`,
      stk_approved: `/panel/stk/${notification.related_id}`,
      stk_rejected: `/panel/stk/${notification.related_id}`,
      stk_document_request: `/panel/stk/${notification.related_id}`,
      meeting: `/panel/toplantilar/${notification.related_id}`,
      announcement: `/panel/duyurular/${notification.related_id}`,
    };
    return links[notification.type] || '#';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bildirimler</h1>
          <p className="text-slate-500">
            {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Tüm bildirimler okundu'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={handleMarkAllAsRead}>
            <CheckCheck className="w-4 h-4 mr-2" />
            Tümünü Okundu İşaretle
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card className="card-shadow">
        <CardContent className="p-0">
          {notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon = getNotificationIcon(notification.type);
                const link = getNotificationLink(notification);
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50 transition-colors ${
                      !notification.is_read ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2.5 rounded-xl flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <Link to={link} className="hover:underline">
                              <p className={`text-sm ${!notification.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                                {notification.message}
                              </p>
                            </Link>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatRelativeTime(notification.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!notification.is_read && (
                              <>
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 border">Yeni</Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMarkAsRead(notification.id)}
                                  title="Okundu olarak işaretle"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Inbox className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Bildirim Yok</h3>
              <p className="text-slate-500 mt-1">Henüz hiç bildiriminiz bulunmuyor</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="card-shadow bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-700">
                Bildirimler aşağıdaki durumlarda gönderilir:
              </p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc list-inside">
                <li>Talebiniz oluşturulduğunda veya güncellendiğinde</li>
                <li>STK başvurunuzun durumu değiştiğinde</li>
                <li>Yeni toplantılar planlandığında</li>
                <li>Önemli duyurular yayınlandığında</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
